import { COMMA } from '@angular/cdk/keycodes';
import { AsyncPipe } from '@angular/common';
import { Component, ElementRef, EventEmitter, Input, Output, ViewChild, OnInit } from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import {
  MatAutocompleteSelectedEvent,
  MatAutocomplete,
  MatAutocompleteTrigger,
  MatAutocompleteModule
} from '@angular/material/autocomplete';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatChipsModule } from '@angular/material/chips';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { Observable, of, Subject, merge } from 'rxjs';
import { debounceTime, distinctUntilChanged, finalize, map, switchMap } from 'rxjs/operators';
import { MatChipInputEvent } from '@angular/material/chips';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { TranslateModule } from '@ngx-translate/core';

@Component({
  selector: 'educare-chips-autocomplete',
  standalone: true,
  imports: [
    MatFormFieldModule,
    MatChipsModule,
    MatIconModule,
    MatAutocompleteModule,
    MatCheckboxModule,
    MatInputModule,
    FormsModule,
    ReactiveFormsModule,
    AsyncPipe,
    MatProgressSpinnerModule,
    TranslateModule
  ],
  templateUrl: './chips-autocomplete.component.html',
  styleUrls: ['./chips-autocomplete.component.scss']
})
export class ChipsAutocompleteComponent implements OnInit {
  @Input() searchFn?: (keyword: string) => Observable<any[]>;
  @Input() placeholder: string = 'eventForm.labels.searchSpeakers';
  @Input() showAllOnClick: boolean = false;
  @Input() allowCustomEntry: boolean = false;
  @Input() emitIdsOnly: boolean = true;

  readonly separatorKeysCodes = [COMMA] as const;
  searchCtrl = new FormControl('');
  filteredOptions: Observable<any[]> = new Observable();

  @Input() options: any[] = [];
  @Input() selectedItems: any[] = [];
  /** If provided, only chips for which this returns true will show the remove (X) icon. Default: all removable. */
  @Input() isItemRemovable: (item: any) => boolean = () => true;
  @Output() emitSelectedOptions = new EventEmitter<any[]>();

  @ViewChild('searchInput') searchInput!: ElementRef<HTMLInputElement>;
  @ViewChild('auto') matAutocomplete!: MatAutocomplete;
  @ViewChild('autocompleteTrigger') matACTrigger!: MatAutocompleteTrigger;
  isLoading = false;
  isExpanded = false;

  /** Emits when the arrow is clicked to open the panel and load all options (e.g. area of interest). */
  private readonly loadAllTrigger$ = new Subject<string>();

  ngOnInit(): void {
    if (this.showAllOnClick) {
      this.filteredOptions = this.searchCtrl.valueChanges.pipe(
        debounceTime(300),
        map((value) => value || ''),
        distinctUntilChanged(),
        map((value: string) => {
          if (!value.trim()) return this.options;
          const filtered = this._filter(value);
          return this.allowCustomEntry ? this.appendCustomOption(filtered, value) : filtered;
        })
      );
    } else {
      const searchInput$ = this.searchCtrl.valueChanges.pipe(map((value) => (value ?? '') as string));
      const openPanelWithEmpty$ = this.loadAllTrigger$.pipe(map(() => ''));
      this.filteredOptions = merge(searchInput$, openPanelWithEmpty$).pipe(
        debounceTime(300),
        distinctUntilChanged(),
        switchMap((value: string) => {
          if (this.searchFn) {
            this.isLoading = true;
            const term = (value || '').trim();
            return this.searchFn(term).pipe(
              map((options: any[]) =>
                this.allowCustomEntry && term ? this.appendCustomOption(options || [], term) : options || []
              ),
              finalize(() => (this.isLoading = false))
            );
          }
          this.isLoading = false;
          return of(this.options);
        })
      );
    }
  }

  private appendCustomOption(options: any[], value: string): any[] {
    const trimmed = value.trim();
    if (!trimmed) return options;
    const nameMatch = (name: string) => name.trim().toLowerCase() === trimmed.toLowerCase();
    const inList = options.some((o) => nameMatch(o?.name ?? ''));
    const alreadySelected = this.selectedItems.some((i) => nameMatch(i?.name ?? ''));
    if (!inList && !alreadySelected) {
      return [...options, { id: null, name: trimmed, _custom: true }];
    }
    return options;
  }

  private _filter(value: any): any[] {
    const filterValue = (typeof value === 'string' ? value : '').toLowerCase();
    return this.options.filter((opt) => opt.name.toLowerCase().includes(filterValue));
  }

  add(event: MatChipInputEvent): void {
    const value = (event.value || '').trim();
    if (
      value &&
      !this.selectedItems.some((item) => (item.name || '').toString().toLowerCase() === value.toLowerCase())
    ) {
      this.selectedItems.push({ id: null, name: value, _custom: true });
      this.emitSelectedOptions.emit(this.emitIdsOnly ? this.selectedItems.map((i) => i.id) : [...this.selectedItems]);
    }
    event.chipInput!.clear();
    this.searchCtrl.setValue('');
  }

  selected(event: MatAutocompleteSelectedEvent): void {
    const selected = event.option.value;
    const exists = this.selectedItems.some((item) =>
      item.id != null && selected.id != null ? item.id === selected.id : item.name === selected.name
    );
    if (!exists) {
      this.selectedItems.push(selected);
      this.emitSelectedOptions.emit(this.emitIdsOnly ? this.selectedItems.map((i) => i.id) : [...this.selectedItems]);
    }
    this.searchInput.nativeElement.value = '';
    this.searchCtrl.setValue('');
    requestAnimationFrame(() => this.openAuto(this.matACTrigger));
  }

  remove(value: any): void {
    const index = this.selectedItems.findIndex((item) => {
      if (isNaN(item.id) && isNaN(value.id)) return item.name === value.name && item.email === value.email;
      return item.id == value.id;
    });

    if (index >= 0) {
      this.selectedItems.splice(index, 1);
      this.emitSelectedOptions.emit(this.emitIdsOnly ? this.selectedItems.map((i) => i.id) : [...this.selectedItems]);
    }
  }

  openAuto(trigger: MatAutocompleteTrigger) {
    trigger.openPanel();
    this.searchInput.nativeElement.focus();
  }

  get displayName() {
    return (item: any) => item?.name || 'Unknown';
  }

  get visibleChips(): any[] {
    return this.isExpanded ? this.selectedItems : this.selectedItems.slice(0, 3);
  }

  toggleExpand(): void {
    this.isExpanded = !this.isExpanded;
  }

  handleExpand(trigger: MatAutocompleteTrigger, event?: Event): void {
    event?.stopPropagation();
    event?.preventDefault();
    if (trigger.panelOpen) {
      trigger.closePanel();
      this.isExpanded = false;
      return;
    }
    this.isExpanded = true;
    if (this.showAllOnClick) {
      this.searchCtrl.setValue('', { emitEvent: true });
    } else if (this.searchFn) {
      this.loadAllTrigger$.next('');
    }
    this.openAuto(trigger);
  }

  get extraCount(): number {
    return this.selectedItems.length > 3 ? this.selectedItems.length - 3 : 0;
  }

  isAlreadySelected(option: any): boolean {
    return this.selectedItems.some((item) => {
      if (item.id == null && option.id == null) {
        return (item.name || '').toString().toLowerCase() === (option.name || '').toString().toLowerCase();
      }
      if (isNaN(item.id) && isNaN(option.id)) return item.name === option.name && item.email === option.email;
      return item.id == option.id;
    });
  }
}
